import { useMemo, useState } from "react";

import { useStokAnaliz } from "@/api/stok";
import { EmptyState } from "@/components/EmptyState";
import { SortableTable } from "@/components/SortableTable";
import { SegmentedTabs } from "@/components/ui";
import { getBedenColumns, getStokUrunColumns } from "@/features/stok/columns";
import { StokKapsam } from "@/features/stok/StokKapsam";
import { StokOzet } from "@/features/stok/StokOzet";
import { StokSeviye } from "@/features/stok/StokSeviye";
import { filterUrunRows, STOK_TABS, stokRowKeyer, urunRowsForTab, type StokTabKey } from "@/features/stok/types";
import { t } from "@/i18n";

/**
 * Baslangic sirasi — backend'in ZATEN uyguladigi sirayla AYNI YONDE olmalidir
 * (SortableTable ilk `direction` HER ZAMAN "asc" ile baslar). Backend
 * kritik/yakin'i kalan_gun ARTAN, az_satan'i satilan ARTAN dondurur (bkz.
 * app/core/analytics/stok.py stok_analiz) — bunlar icin initialSortKey
 * guvenlidir. cok_satan (satilan AZALAN) ve beden_analizi (toplam_satilan
 * AZALAN) ise tam TERSI yonde siralanmis gelir; "tumu" TUM gruplarin
 * concat'idir (tek bir sutuna gore "artan" varsayimi anlamsiz) — bu ucu
 * icin initialSortKey BILEREK verilmez (sortKey=null -> SortableTable
 * satirlari OLDUGU GIBI birakir, backend/concat sirasi korunur).
 */
const INITIAL_SORT: Partial<Record<StokTabKey, string>> = {
  kritik: "kalan_gun",
  yakin: "kalan_gun",
  az_satan: "satilan",
};

/**
 * /stok — stok analizi sekmeleri (docs/faz5-arayuz-spec.md §2 + eski
 * arayuzle esitlik). Kategoriler `/api/v2/stok/analiz`'tan
 * (kritik/yakin/cok_satan/az_satan/beden/tumu), "seviye" sekmesi ise STOK
 * MIKTARI bazli ayrı bir gorunum — `useKpiSnapshot` ->
 * `kpis.kritik_stok.subitems` (bkz. features/stok/StokSeviye.tsx,
 * features/stok/seviye.ts).
 */
export function Stok() {
  const analizQuery = useStokAnaliz();
  const [tab, setTab] = useState<StokTabKey>("kritik");
  const [q, setQ] = useState("");

  const urunColumns = useMemo(() => getStokUrunColumns(), []);
  const tumuColumns = useMemo(() => getStokUrunColumns({ grup: true }), []);
  const bedenColumns = useMemo(() => getBedenColumns(), []);

  if (analizQuery.isLoading) {
    return <p className="text-sm text-muted">{t("common.loading")}</p>;
  }
  if (analizQuery.isError) {
    return (
      <p role="alert" className="text-sm text-red">
        {t("common.error")}
      </p>
    );
  }

  const data = analizQuery.data;
  if (!data) return null;

  if ("status" in data) {
    return <EmptyState kind="not_supported" />;
  }
  if (!data.ready) {
    return <><InventoryCoverage coverage={data.coverage} /><EmptyState kind="no_data" /></>;
  }

  return (
    <section className="space-y-6">
      <h1 className="font-display text-xl font-bold text-navy">{t("nav.stok")}</h1>

      <StokOzet ozet={data.ozet} />
      <p className="text-sm text-muted">Depodaki fiziksel stok: <strong className="num text-ink">{data.ozet.toplam_stok.toLocaleString('tr-TR')} adet</strong>. Paketlerin satılabilir adedi bileşenlerden hesaplanır; depo toplamına yeniden eklenmez.</p>
      <InventoryCoverage coverage={data.coverage} />
      <StokKapsam satisaKapali={data.satisa_kapali} ciftKartlar={data.cift_kartlar} />

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedTabs
          value={tab}
          onChange={setTab}
          ariaLabel={t("nav.stok")}
          options={STOK_TABS.map((tabDef) => ({ value: tabDef.key, label: t(tabDef.labelKey) }))}
        />

        {tab !== "beden" && (
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("stok.ara_placeholder")}
            aria-label={t("stok.ara_placeholder")}
            className="input max-w-sm"
          />
        )}
      </div>

      {tab === "beden" && (
        <SortableTable columns={bedenColumns} rows={data.beden_analizi} rowKey={(r) => r.beden} stickyFirstColumn />
      )}

      {tab === "seviye" && <StokSeviye q={q} />}

      {tab !== "beden" &&
        tab !== "seviye" &&
        (() => {
          const rows = filterUrunRows(urunRowsForTab(data, tab), q);
          const columns = tab === "tumu" ? tumuColumns : urunColumns;
          return (
            // key={tab}: sekmeler arasi gecerken SortableTable'in kendi ic
            // siralama durumu (useState) YENIDEN BASLAMALI — aksi halde ör.
            // "Kritik" sekmesinden initialSortKey="kalan_gun" ile gelinip
            // "Tumu"ya gecilince eski sortKey/direction TASINIR ve backend/
            // concat sirasi yerine yanlis bir sutuna gore siralanmis gorunur
            // (inceleme bulgusu).
            <SortableTable
              key={tab}
              columns={columns}
              rows={rows}
              rowKey={stokRowKeyer(rows)}
              initialSortKey={INITIAL_SORT[tab]}
              stickyFirstColumn
              pageSize={25}
            />
          );
        })()}
    </section>
  );
}

function InventoryCoverage({ coverage }: { coverage?: { excluded_count: number; unknown_count: number; items: { variant_native_id: string; name: string; stock_kind: string }[] } }) {
  if (!coverage || (!coverage.excluded_count && !coverage.unknown_count)) return null;
  return <details className="rounded-lg border border-line bg-white p-4 text-sm">
    <summary className="cursor-pointer font-semibold">Stok kapsamı · {coverage.excluded_count} fiziksel olmayan kayıt hariç · {coverage.unknown_count} hesaplanamayan stok</summary>
    <p className="mt-2 text-muted">İade takibi için açılan dijital kayıtlar kritik stok sayılmaz. Bileşen bilgisi veya stok verisi eksik ürünler sıfır stok olarak gösterilmez.</p>
    <ul className="mt-3 space-y-2">{coverage.items.map(item => <li key={item.variant_native_id}>{item.name} <span className="text-muted">— {item.stock_kind === 'excluded' ? 'fiziksel stok dışında' : item.stock_kind === 'bundle_unmapped' ? 'paket bileşeni gerekli' : 'stok bilgisi eksik'}</span></li>)}</ul>
  </details>;
}
